import { track } from '@amplitude/analytics-browser'

import { getClientEnv } from '@/env/client'
import { reportUserData } from '@/services/gtm'
import { readSnowplowIds } from '@/services/snowplow'
import type { SnowplowIds, WapiUserLocation } from '@/services/wapi'

import { readVisitorLocation } from './visitor-location'

interface TrackInput {
    email: string
    locale: string
    originCity: string
    destinationCity: string
    transportMode: string
    /**
     * Where the signup occurred, e.g. `landing_c2c`. One value, two destinations:
     * Amplitude's `subscription_source` and — forwarded verbatim by the endpoint —
     * the WAPI event's `signup_source`.
     */
    subscriptionSource: string
}

async function reportSignupIdentity(email: string) {
    if (typeof window === 'undefined') {
        return
    }
    const outcome = await reportUserData({ email })
    if (outcome === 'failed') {
        getClientEnv().logger.warn('signup user_data not reported')
    }
}

/**
 * Amplitude `Newsletter Subscribed`, per the strategy data dictionary
 * (amplitude-events-post-booking.md). The Snowplow ids are not set here: the
 * SnowplowIdsPlugin enriches every event with domain_user_id / network_user_id.
 */
function trackAmplitude(input: TrackInput) {
    // track() is a safe no-op (never throws) when the SDK isn't initialized —
    // e.g. an ad-blocker blocked it — so this call needs no guard or try/catch.
    track('Newsletter Subscribed', {
        arrive_city: input.destinationCity,
        depart_city: input.originCity,
        email_address: input.email,
        mode_type: input.transportMode,
        subscription_source: input.subscriptionSource,
    })
}

async function submitToServer(
    input: TrackInput,
    snowplowIds: SnowplowIds,
    location: WapiUserLocation | undefined,
): Promise<{ ok: boolean }> {
    try {
        const response = await fetch('/api/nexus/newsletter-subscriptions', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...input, snowplowIds, location }),
        })
        return { ok: response.ok }
    } catch (error) {
        getClientEnv()
            .logger.withMetadata({ error: error instanceof Error ? error.message : String(error) })
            .warn('Newsletter subscription POST failed')
        return { ok: false }
    }
}

/**
 * Fires the three-action newsletter subscription pipeline:
 * - the hashed identity onto the tag-manager queue (defensive — works before GTM loads; see nexus#244)
 * - Amplitude `Newsletter Subscribed`
 * - POST to /api/nexus/newsletter-subscriptions -> WAPI -> Kafka -> messaging -> Blueshift
 *
 * Returns { ok } based only on the server submission. Amplitude + the queue push
 * are fire-and-forget: marketing attribution is valuable even on transient
 * API failures.
 *
 * The visitor's location is resolved **here, in the browser**, and sent with the
 * signup: WAPI geolocates whichever IP it sees, and only a request the visitor's own
 * device makes carries theirs. `readLocation` is injected in tests; a failed lookup
 * yields no location and never blocks the signup.
 */
export async function trackNewsletterSubscription(
    input: TrackInput,
    readLocation: () => Promise<WapiUserLocation | undefined> = readVisitorLocation,
): Promise<{ ok: boolean }> {
    // Deliberately not awaited: the signup is not waiting on a match signal, and
    // `reportSignupIdentity` never rejects.
    void reportSignupIdentity(input.email)
    trackAmplitude(input)
    const snowplowIds = readSnowplowIds()
    const location = await readLocation()
    return submitToServer(input, snowplowIds, location)
}
